import React from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import breakpoints from "_constants/breakpoints";

const Container = styled.div`
  padding: 36px 32px 36px;
  margin-bottom: 24px;
  background: #ffffff;
  box-shadow: 0px 3px 25px rgba(168, 168, 168, 0.3);

  @media (max-width: ${breakpoints.mobileM}) {
    padding: 26px 12px 12px;

    & p {
      font-size: 14px;
      text-align: justify;
    }
  }
`;
const Title = styled.h1`
  margin-bottom: 16px;
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;
  color: #7b848d;

  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      @media (max-width: ${breakpoints.mobileM}) {
        font-size: 16px;
        text-align: center;
      }
    `}

  & div {
    display: inline-block;
  }
`;

const SubTitle = styled.h2`
  font-weight: bold;
  font-size: 16px;
  color: #7b848d;

  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      @media (max-width: ${breakpoints.mobileM}) {
        text-align: center;
      }
    `}
`;

const AssessmentRow = styled.div`
  padding: 12px;
  box-shadow: 0 0 5px lightgray inset;
  margin-bottom: 16px;
  border-radius: 3px;
`;

const AssessmentStatus = styled.p`
  color: green;
  font-weight: bold;

  ${({ status }) =>
    status &&
    css`
      color: orange;
    `}

  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      @media (max-width: ${breakpoints.mobileM}) {
        text-align: center !important;
      }
    `}
`;

const InfoContainer = styled.div``;

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;

  margin: 16px 0;
`;

const Dot = styled.div`
  background-color: #7b848d;
  width: 12px;
  height: 12px;
  border-radius: 50%;

  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      @media (max-width: ${breakpoints.mobileM}) {
        width: 8px;
        height: 8px;
      }
    `}
`;

const Dots = () => (
  <DotsContainer>
    <Dot />
    <Dot />
    <Dot />
  </DotsContainer>
);

function Assessment({ assessments }) {
  return (
    <Container>
      <p>
        Our Legal Team has conducted a thorough evaluation of your proposed trademark, along with a detailed analysis of
        the competitive landscape. We are delighted to present the findings and guidance in this report.
      </p>

      <AssessmentRow>
        <Title>
          <div>Trademark Assessment</div> <div>(Search Review)</div>
        </Title>
        <AssessmentStatus status={assessments.trademarkAssessment.value}>
          {assessments.trademarkAssessment.value ? "Potential Concern" : "No Concerns"}
        </AssessmentStatus>
        {!assessments.trademarkAssessment.value ? (
          <InfoContainer>
            <p>
              Our team has thoroughly examined your proposed trademark to identify any potential challenges and
              opportunities for enhancement. After an extensive search review, we are pleased to inform you that no
              conflicting trademarks or issues were found. Your proposed trademark appears to be unique and distinct
              within its class.
            </p>
          </InfoContainer>
        ) : (
          <InfoContainer>
            <SubTitle>Note From Our Legal Team:</SubTitle>
            <p>{assessments.trademarkAssessment.description || "–"}</p>
          </InfoContainer>
        )}
      </AssessmentRow>

      <Dots />

      <AssessmentRow>
        <Title>
          <div>Competitive Analysis</div> <div>(Review of Competitors)</div>
        </Title>
        <AssessmentStatus status={assessments.competitiveAnalysis.value}>
          {assessments.competitiveAnalysis.value ? "Potential Concern" : "No Concerns"}
        </AssessmentStatus>
        {!assessments.competitiveAnalysis.value ? (
          <InfoContainer>
            <p>
              We conducted a comprehensive review of existing trademarks in your industry to ensure that your brand
              distinguishes itself effectively. We are delighted to report that your proposed trademark stands out from
              the competition. It does not infringe upon any existing trademarks, and it positions your brand favorably
              in the market.
            </p>
          </InfoContainer>
        ) : (
          <InfoContainer>
            <SubTitle>Note From Our Legal Team:</SubTitle>
            <p>{assessments.competitiveAnalysis.description || "–"}</p>
          </InfoContainer>
        )}
      </AssessmentRow>

      <Dots />

      <AssessmentRow>
        <Title>
          <div>Strategic Guidance</div> <div>(Recommendations & Potential Issues)</div>
        </Title>
        <AssessmentStatus status={assessments.strategicGuidance.value}>
          {assessments.strategicGuidance.value ? "Potential Concern" : "No Concerns"}
        </AssessmentStatus>
        {!assessments.strategicGuidance.value ? (
          <InfoContainer>
            <p>
              Given the positive results from the trademark assessment and competitive analysis, we have limited
              specific recommendations for your trademark application. However, we would like to offer some general
              strategic guidance to enhance the likelihood of your trademark gaining approval:
            </p>
            <ol>
              <li>
                Trademark Registration: We recommend proceeding with the trademark registration process as soon as
                possible to secure your brand&apos;s exclusive rights.
              </li>
              <li>
                Regular Monitoring: Even though no issues were found during our assessment, it&apos;s advisable to
                conduct periodic trademark searches to ensure continued protection.
              </li>
              <li>
                Brand Identity: Consider developing a strong brand identity strategy to reinforce the distinctiveness of
                your trademark.
              </li>
              <li>
                Market Expansion: As your business grows, monitor potential trademark conflicts that may arise in new
                markets or product/service expansions.
              </li>
            </ol>
          </InfoContainer>
        ) : (
          <InfoContainer>
            <SubTitle>Note From Our Legal Team:</SubTitle>
            <p>{assessments.strategicGuidance.description || "–"}</p>
          </InfoContainer>
        )}
      </AssessmentRow>

      <p>
        If you have any questions or would like to discuss these findings in more detail, please do not hesitate to
        contact us. We are committed to helping you protect and maximize the value of your intellectual property.
      </p>
    </Container>
  );
}

Assessment.propTypes = {
  assessments: PropTypes.object,
};

export default Assessment;
